import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Download, ImageOff, Lock, Loader2, X } from 'lucide-react';
import { WhatsAppMessage, whatsappInboxService } from '@/services/integrations/whatsappInboxService';

const basenameOf = (key: string | null): string => {
  if (!key) return 'Document';
  const parts = key.split('/');
  return parts[parts.length - 1] || 'Document';
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; url: string }
  | { kind: 'denied' }
  | { kind: 'unavailable' };

/**
 * Full-size viewer for an image already fetched into a blob URL.
 *
 * Rendered through a portal on document.body: the chat bubble it is launched
 * from sets `overflow-hidden` to round its corners, and the thread scrolls,
 * so an overlay left in place would be clipped to the bubble.
 */
const ImageLightbox: React.FC<{ url: string; filename: string; onClose: () => void }> = ({
  url, filename, onClose,
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // the thread behind the overlay shouldn't scroll under the pointer
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return createPortal(
    <div
      onClick={onClose}
      className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-6"
    >
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <a
          href={url}
          download={filename}
          onClick={(e) => e.stopPropagation()}
          title="Download"
          className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
        >
          <Download className="w-4 h-4" />
        </a>
        <button
          onClick={onClose}
          title="Close"
          className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <img
        src={url}
        alt={filename}
        // stopPropagation so clicking the photo itself doesn't dismiss it —
        // only the backdrop around it does
        onClick={(e) => e.stopPropagation()}
        className="max-w-full max-h-full object-contain rounded shadow-2xl"
      />
    </div>,
    document.body,
  );
};

/**
 * Image/video/document/audio renderer (FE-11).
 *
 * Fetches the media as an authenticated blob (whatsappInboxService.fetchMediaBlob)
 * rather than pointing an <img>/<video>/<audio> tag straight at the media URL —
 * a plain tag's onerror can't tell a 403 (no lead_scope access to this
 * conversation) apart from a genuinely missing file (media_storage_key never
 * set, or the object is gone from storage), so every failure rendered as the
 * same misleading "Media unavailable", even when the file was perfectly fine
 * and the viewer just isn't allowed to see this particular lead's data.
 */
const MediaAttachment: React.FC<{ message: WhatsAppMessage }> = ({ message }) => {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const filename = basenameOf(message.media_storage_key);

  useEffect(() => {
    if (!message.media_storage_key) {
      setState({ kind: 'unavailable' });
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setState({ kind: 'loading' });
    whatsappInboxService.fetchMediaBlob(message.id).then((result) => {
      if (cancelled) return;
      if (result.status === 'ok') {
        objectUrl = result.url;
        setState({ kind: 'ready', url: result.url });
      } else {
        setState({ kind: result.status });
      }
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [message.id, message.media_storage_key]);

  if (state.kind === 'loading') {
    return (
      <div className="w-[220px] h-[110px] rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (state.kind === 'denied') {
    return (
      <div className="w-[220px] h-[110px] rounded-lg bg-gray-100 border border-gray-200 flex flex-col items-center justify-center gap-1.5 text-gray-400">
        <Lock className="w-6 h-6" />
        <span className="text-[11px] font-medium text-center px-2">You don't have access to this conversation's media</span>
      </div>
    );
  }

  if (state.kind === 'unavailable') {
    return (
      <div className="w-[220px] h-[110px] rounded-lg bg-gray-100 border border-gray-200 flex flex-col items-center justify-center gap-1.5 text-gray-400">
        <ImageOff className="w-6 h-6" />
        <span className="text-[11px] font-medium">Media unavailable</span>
      </div>
    );
  }

  const url = state.url;
  switch (message.msg_type) {
    case 'image':
    case 'sticker':
      return (
        <>
          {/* object-contain, not object-cover: a phone screenshot is far taller
              than the 320px thumbnail box, and cover silently crops the middle
              out of it. Contain shows the whole frame, letterboxed. */}
          <img
            src={url}
            alt={message.body || 'attachment'}
            onClick={() => setLightboxOpen(true)}
            title="Click to view full size"
            className="max-w-[280px] max-h-[320px] rounded-lg border border-gray-200 object-contain cursor-zoom-in hover:opacity-95"
          />
          {lightboxOpen && (
            <ImageLightbox url={url} filename={filename} onClose={() => setLightboxOpen(false)} />
          )}
        </>
      );
    case 'video':
      return (
        <video src={url} controls className="max-w-[280px] max-h-[320px] rounded-lg border border-gray-200" />
      );
    case 'audio':
    case 'voice':
      return <audio src={url} controls className="max-w-[280px]" />;
    case 'document':
    default:
      return (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 max-w-[280px]"
        >
          <FileText className="w-6 h-6 text-gray-500 flex-shrink-0" />
          <span className="text-[12px] text-gray-700 truncate flex-1">{filename}</span>
          <Download className="w-4 h-4 text-gray-400 flex-shrink-0" />
        </a>
      );
  }
};

export default MediaAttachment;
